/**
 * Cliente HTTP para comunicacao com a API PayFlow
 * Modulo puro - sem dependencias de React/browser frameworks
 * Futuro: app mobile tera seu proprio api.ts com mesma interface usando AsyncStorage
 */

const API_BASE_URL = (import.meta.env.VITE_API_URL || "http://localhost:8787") + "/v1/admin";

const TOKEN_KEY = "payflow_token";

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: {
    code: string;
    message: string;
    details?: Record<string, string>;
  };
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export async function apiClient<T = unknown>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const { body, headers: customHeaders, ...restOptions } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...customHeaders as Record<string, string>,
  };

  const token = getToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...restOptions,
    headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  if (response.status === 401 && !endpoint.includes("/auth/login")) {
    removeToken();
    localStorage.removeItem("payflow_user");
    window.location.href = "/login";
    throw new Error("Sessao expirada. Faca login novamente.");
  }

  const data: ApiResponse<T> = await response.json();

  if (!response.ok || !data.success) {
    const message = data.error?.message || "Erro inesperado";
    throw new Error(message);
  }

  return data;
}

export const api = {
  get: <T = unknown>(endpoint: string) =>
    apiClient<T>(endpoint, { method: "GET" }),

  post: <T = unknown>(endpoint: string, body?: unknown) =>
    apiClient<T>(endpoint, { method: "POST", body }),

  put: <T = unknown>(endpoint: string, body?: unknown) =>
    apiClient<T>(endpoint, { method: "PUT", body }),

  delete: <T = unknown>(endpoint: string) =>
    apiClient<T>(endpoint, { method: "DELETE" }),
};
